import React, { useEffect } from "react"
// eslint-disable-next-line no-restricted-imports
import { useBreakpointComponent } from "@carrotfertility/carotene" // please do not import from @carrotfertility/carotene after eslint rule added on 3/20/25

import { ReadAndLearnBreadcrumbs } from "./shared/ReadAndLearnBreadcrumbs"
import { EmbeddedVideo } from "./EmbeddedVideo"
import { FormattedMessage, useIntl } from "react-intl"
import { carrotDisclaimerMessages } from "#/utils/CarrotDisclaimers"
import { ContentfulContentRenderer, ContentfulContentEntry, ContentfulRawMarkdownRenderer } from "#/services/contentful"
import { defaultMarkdownElements } from "@internal/ui"
import { Box, Divider, Tile, Typography, useTheme } from "@carrotfertility/carotene-core"
import { Title } from "#/services/page-title/Title"
import { PageLayout } from "#/features/global-ui/page-layout"
import { PageBreadcrumbHeader } from "#/features/global-ui/page-header"
import { EntryWithoutUnresolvableLinks } from "#/utils/ContentTypes"
import { TypeCategorySkeleton, TypeSubCategorySkeleton } from "#/types/contentful"
import { CarrotAcademySearch } from "./CarrotAcademySearch"
import { useMemberActionTracking } from "#/services/memberActionTracking/useMemberActionTracking"
import { useLocation } from "react-router-dom"
import { MemberActions } from "#/services/memberActionTracking/memberActions"
import { FavoriteButton } from "./FavoriteButton"
import { FeedbackContainer } from "./FeedbackContainer"
type LastUpdatedTagProps = {
  lastUpdated: string
  isVideo: boolean
}

function LastUpdatedTag({ lastUpdated, isVideo }: LastUpdatedTagProps): JSX.Element {
  const intl = useIntl()
  const theme = useTheme()
  const lastUpdatedDate = intl.formatDate(lastUpdated, { year: "numeric", month: "2-digit", day: "2-digit" })
  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={(theme) => theme.spacing(theme.tokens.spacing.sm)}
      padding={isVideo ? "40px 0px 0px 0px" : "48px 0px 0px 0px"}
    >
      <Box width={47}>
        <Divider sx={{ borderBlockEndWidth: 3, borderColor: theme.palette.border.light }} />
      </Box>
      <Typography variant="body2">
        <FormattedMessage
          defaultMessage="Last updated: {lastUpdatedDate}"
          description="The date for when the article was last updated"
          values={{ lastUpdatedDate }}
        />
      </Typography>
    </Box>
  )
}

function ArticleLegalSidebar(): JSX.Element {
  const intl = useIntl()
  const dividerBottomSidebar = useBreakpointComponent([
    <Box padding={"48px 0px 24px 0px"} key={"smallest"}>
      <Divider sx={{ borderBlockEndWidth: 3 }} />
    </Box>,
    <Box padding={"48px 0px 24px 0px"} key={"small"}>
      <Divider sx={{ borderBlockEndWidth: 3 }} />
    </Box>,
    <Box padding={"48px 0px 24px 0px"} key={"medium"}>
      <Divider sx={{ borderBlockEndWidth: 3 }} />
    </Box>,
    <Box height={"48px"} key={"large"} />
  ])
  return (
    <>
      {dividerBottomSidebar}
      <Tile>
        <Box
          color={(theme) => theme.palette.text.tertiary}
          display="flex"
          flexDirection="column"
          gap={(theme) => theme.spacing(theme.tokens.spacing.lg)}
        >
          <Typography variant="body2" color="inherit">
            {intl.formatMessage(carrotDisclaimerMessages.SERVICES_LEGALITY_AVAILABILITY)}
          </Typography>
          <Divider />
          <Typography variant="body2" color="inherit">
            {intl.formatMessage(carrotDisclaimerMessages.INCLUSIVE_LANGUAGE)}
          </Typography>
        </Box>
      </Tile>
    </>
  )
}

type MainContentProps = {
  articleBody: string
  articleTitle: string
  isVideo: boolean
  videoUrl: string
  articleContent?: Array<ContentfulContentEntry> | null
}

const MainContent = ({
  articleBody,
  articleContent,
  articleTitle,
  isVideo,
  videoUrl
}: MainContentProps): JSX.Element => {
  if (isVideo) {
    return <EmbeddedVideo videoUrl={videoUrl} title={articleTitle} />
  } else if (articleContent) {
    return <ContentfulContentRenderer contents={articleContent} />
  } else {
    return <ContentfulRawMarkdownRenderer>{articleBody}</ContentfulRawMarkdownRenderer>
  }
}

type ReadArticleProps = {
  articleBody: string
  articleTitle: string
  category: EntryWithoutUnresolvableLinks<TypeCategorySkeleton>
  isVideo: boolean
  lastUpdated: string
  subCategory: EntryWithoutUnresolvableLinks<TypeSubCategorySkeleton>
  videoUrl: string
  articleContent?: Array<ContentfulContentEntry> | null
  entryId: string
  contentType: string
}

export default function ReadLearnArticle({
  articleBody,
  articleTitle,
  category,
  isVideo,
  lastUpdated,
  subCategory,
  videoUrl,
  articleContent,
  entryId,
  contentType
}: ReadArticleProps): JSX.Element {
  const intl = useIntl()

  const dividerSideSidebar = useBreakpointComponent([
    null,
    null,
    null,
    <Box padding={"48px 0px 0px 0px"} key={"large"}>
      <Divider />
    </Box>
  ])
  const theme = useTheme()
  const location = useLocation()
  const { mutate: addMemberAction } = useMemberActionTracking()

  useEffect(() => {
    addMemberAction({
      memberActionTaken: isVideo
        ? MemberActions.CARROT_ACADEMY_VIDEO_VIEWED
        : MemberActions.CARROT_ACADEMY_ARTICLE_VIEWED,
      metaData: {
        category: category?.fields?.navTitle,
        url: location.pathname,
        entryId
      }
    })
  }, [location.pathname, category, entryId, isVideo, addMemberAction])

  return (
    <>
      <Title title={articleTitle} productTitle="Carrot Academy" />
      <PageLayout
        variant="sidebar"
        sidebar={<ArticleLegalSidebar />}
        header={
          <PageBreadcrumbHeader
            breadcrumbs={<ReadAndLearnBreadcrumbs category={category} subcategory={subCategory} />}
            search={<CarrotAcademySearch />}
          />
        }
      >
        <Box maxWidth={isVideo ? "800px" : "606px"} alignItems={"center"}>
          <Box
            sx={(theme) => ({
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: theme.tokens.spacing.sm
            })}
          >
            <defaultMarkdownElements.h1.component {...defaultMarkdownElements.h1.props}>
              {articleTitle}
            </defaultMarkdownElements.h1.component>
            <FavoriteButton contentfulEntryId={entryId} categoryEntryId={category.sys.id} contentType={contentType} />
          </Box>
          <MainContent
            articleBody={articleBody}
            articleContent={articleContent}
            articleTitle={articleTitle}
            isVideo={isVideo}
            videoUrl={videoUrl}
          />
          <LastUpdatedTag lastUpdated={lastUpdated} isVideo={isVideo} />
        </Box>
        {dividerSideSidebar}
        <Box display="flex" flexDirection="column" padding={"24px 0px 0px 0px"} maxWidth={"600px"} width="100%">
          <Box
            sx={(theme) => ({
              marginBlockStart: theme.tokens.spacing.lg,
              marginBlockEnd: theme.tokens.spacing.md
            })}
          >
            <FeedbackContainer
              contentfulEntryId={entryId}
              categoryEntryId={category.sys.id}
              contentType={contentType}
            />
          </Box>
          <Typography variant="body2" color={theme.palette.text.tertiary}>
            {intl.formatMessage(carrotDisclaimerMessages.CARROT_RESOURCES)}
          </Typography>
        </Box>
      </PageLayout>
    </>
  )
}
